import { ChevronLeftIcon } from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import { AnimatePresence, motion } from "motion/react";
import React, { useCallback, useMemo, useState } from "react";

import { List, ListItem } from "../../../components/Data/List";
import { ListRail } from "../../../components/ListRail";
import { IndicationUnit } from "../../../components/Metrics/IndicationUnit";
import { RailHeader } from "../../../components/RailHeader";
import { Button } from "../../../components/ui/button";
import { ToggleMenu, ToggleMenuItem } from "../../../components/ui/toggle-menu";
import { useSimCursor } from "../../sim-cursor/SimCursor";

import { framable, type FramableProps } from "./framable";
import { NB_ACTIVE_PROGRAMS, type NeobankProgramRow } from "./neobankFixtures";
import { NeobankContinuousDetailView } from "./NeobankProgramsOutcomeSim";
import { type SimScriptStep, useSimScript } from "./useSimScript";

/**
 * Neobank scenario — **Mobile: select a program (single-screen master-detail)**.
 *
 * The narrow-viewport counterpart to {@link NeobankProgramsOutcomeSim}. Where
 * the desktop outcome renders the Programs page as a static two-pane
 * rail+detail, this reproduces the app's **single-screen master-detail** flow
 * (`apps/yaas-client/src/pages/Programs/Programs.tsx` below its 576px
 * breakpoint): the program LIST fills the screen, and selecting a program
 * PUSHES to a full-screen detail that slides in from the right with a back bar.
 *
 * A {@link useSimCursor} tour auto-plays it: the cursor glides from +New
 * Program down to the **Continuous Balance Rewards** row, clicks it, and the
 * detail slides in. It holds on the detail ~5s, then the whole sim fades out
 * and the loop replays from the list.
 *
 * Self-contained — the single-screen behaviour in the real page is
 * viewport-driven (`useIsMobileViewport` / `matchMedia`), which a fixed narrow
 * sim FRAME can't trip (the media query reads the window, not the frame). So
 * this owns its master-detail state directly (fixtures only — no wallet / RPC /
 * orgApiFetch / viewport hook), exactly as the desktop outcome owns its
 * two-pane layout. The detail screen reuses {@link NeobankContinuousDetailView}
 * verbatim, so the Continuous read view is identical across both blocks.
 */

/** The Continuous Balance Rewards row — the tour's target. */
const CONTINUOUS_ROW_ID = NB_ACTIVE_PROGRAMS[0]?.id ?? "";
/** Cursor-tour target keys. */
const ROW_KEY = "nb-mobile-continuous-row";
const NEW_PROGRAM_KEY = "nb-mobile-new-program";

// ── Beat timing (ms) ──────────────────────────────────────────────────────
/** List holds still before the cursor appears, so the viewer reads it first. */
const INITIAL_DWELL_MS = 800;
/** Cursor appears at +New Program (top-right), then glides to the row. */
const APPEAR_HOLD_MS = 320;
const TRAVEL_MS = 700;
const HOVER_HOLD_MS = 480;
/** Let the detail finish sliding in before the cursor fades out. */
const SLIDE_SETTLE_MS = 520;
/** Dwell on the detail before the loop fades out — the requested ~5s. */
const DETAIL_HOLD_MS = 5000;
/** Matches the `duration-500` fade on the root wrapper. */
const FADE_MS = 500;
/** Collapse back to the list WHILE faded out (invisible), so the replay is a
 *  clean fade-in on the list, not a visible slide-back. */
const COLLAPSE_AFTER_FADE_MS = 620;
const FADE_IN_AFTER_COLLAPSE_MS = 160;

function NeobankProgramsMobileSimView(): React.ReactElement {
    // `faded` drives the root opacity for the fade-out → replay loop; `detailOpen`
    // is the single-screen drill-in (list ↔ detail); `hoverRow` paints the row's
    // forced hover as the cursor lands on it.
    const [faded, setFaded] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [hoverRow, setHoverRow] = useState<string | null>(null);

    const cursor = useSimCursor();
    const { moveTo: moveCursor, click: clickCursor, hide: hideCursor } = cursor;

    const reset = useCallback((): void => {
        setFaded(false);
        setDetailOpen(false);
        setHoverRow(null);
        hideCursor();
    }, [hideCursor]);

    const steps = useMemo<readonly SimScriptStep[]>(
        () => [
            // Cursor appears at +New Program (a near-instant place, not a fly-in
            // from the seeded rest), so every loop starts the glide from the same
            // spot regardless of where it was hidden.
            {
                after: INITIAL_DWELL_MS,
                label: "mobile:appear",
                run: (): void =>
                    moveCursor(NEW_PROGRAM_KEY, {
                        cursor: "pointer",
                        durationMs: 1
                    })
            },
            // Glide down to the Continuous Balance Rewards row.
            {
                after: APPEAR_HOLD_MS,
                label: "mobile:travel",
                run: (): void =>
                    moveCursor(ROW_KEY, {
                        cursor: "pointer",
                        durationMs: TRAVEL_MS
                    })
            },
            {
                after: TRAVEL_MS,
                label: "mobile:hover",
                run: (): void => setHoverRow(CONTINUOUS_ROW_ID)
            },
            // Click → push to the detail screen (slides in from the right).
            {
                after: HOVER_HOLD_MS,
                label: "mobile:press",
                run: (): void => {
                    clickCursor();
                    setDetailOpen(true);
                    setHoverRow(null);
                }
            },
            // The detail is up — retire the cursor while it's read.
            {
                after: SLIDE_SETTLE_MS,
                label: "mobile:cursor-out",
                run: (): void => hideCursor()
            },
            {
                after: DETAIL_HOLD_MS,
                label: "mobile:hold",
                run: (): void => {}
            },
            // Fade the whole sim out, collapse back to the list while invisible,
            // then fade the list back in — a clean replay, no visible slide-back.
            {
                after: 0,
                label: "mobile:fade-out",
                run: (): void => setFaded(true)
            },
            {
                after: FADE_MS + COLLAPSE_AFTER_FADE_MS,
                label: "mobile:collapse",
                run: (): void => setDetailOpen(false)
            },
            {
                after: FADE_IN_AFTER_COLLAPSE_MS,
                label: "mobile:fade-in",
                run: (): void => setFaded(false)
            }
        ],
        [moveCursor, clickCursor, hideCursor]
    );

    useSimScript({ reset, steps });

    const listScreen = (
        <ListRail className="w-full border-r-0">
            <RailHeader
                count={
                    <Text variant="small" className="text-foreground-muted">
                        {`${NB_ACTIVE_PROGRAMS.length} programs`}
                    </Text>
                }
                action={
                    <Button
                        variant="raised"
                        interaction="lifted"
                        size="xs"
                        data-sim-cursor-key={NEW_PROGRAM_KEY}
                    >
                        +New Program
                    </Button>
                }
            />
            <div className="px-4 pb-3">
                <ToggleMenu value="active" onChange={() => {}} spacing={1}>
                    <ToggleMenuItem value="all" variant="toggle" size="sm">
                        All
                    </ToggleMenuItem>
                    <ToggleMenuItem value="active" variant="toggle" size="sm">
                        Active
                    </ToggleMenuItem>
                    <ToggleMenuItem value="draft" variant="toggle" size="sm">
                        Draft
                    </ToggleMenuItem>
                </ToggleMenu>
            </div>
            <List selectable="single" selected="" onSelect={() => {}}>
                {NB_ACTIVE_PROGRAMS.map((program) => (
                    <MobileProgramRow
                        key={program.id}
                        program={program}
                        cursorKey={
                            program.id === CONTINUOUS_ROW_ID
                                ? ROW_KEY
                                : undefined
                        }
                        forceHover={hoverRow === program.id}
                    />
                ))}
            </List>
        </ListRail>
    );

    const detailScreen = (
        <div className="flex h-full flex-col">
            {/* Static back bar — the mobile detail's app-bar affordance. The
                tour returns via the fade-out loop, so it's presentational. */}
            <div className="flex shrink-0 items-center gap-1.5 border-b border-line-base bg-surface-ground px-4 py-3 text-foreground-muted">
                <ChevronLeftIcon className="size-4" />
                <Text variant="small">All programs</Text>
            </div>
            <NeobankContinuousDetailView />
        </div>
    );

    return (
        <div
            ref={cursor.containerRef}
            className={`relative h-full overflow-hidden transition-opacity duration-500 ${
                faded ? "opacity-0" : "opacity-100"
            }`}
        >
            <AnimatePresence initial={false}>
                {detailOpen ? (
                    <motion.div
                        key="detail"
                        className="absolute inset-0"
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={SLIDE_TRANSITION}
                    >
                        {detailScreen}
                    </motion.div>
                ) : (
                    <motion.div
                        key="list"
                        className="absolute inset-0 flex"
                        initial={{ x: "-100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "-100%" }}
                        transition={SLIDE_TRANSITION}
                    >
                        {listScreen}
                    </motion.div>
                )}
            </AnimatePresence>
            {cursor.layer}
        </div>
    );
}

/** Push-transition easing — mirrors the app's single-screen master-detail
 *  slide in `Programs.tsx`. */
const SLIDE_TRANSITION = {
    type: "tween" as const,
    ease: [0.16, 1, 0.3, 1] as const,
    duration: 0.28
};

/**
 * One program row in the mobile list — a dependency-free equivalent of the
 * app's `ProgramRow` (status dot + name on the top line, region on the
 * sub-line). Carries an optional `data-sim-cursor-key` on its content so the
 * tour cursor can target it, and forwards `forceHover` to the `ListItem` so the
 * row paints its hover as the cursor lands.
 */
function MobileProgramRow({
    program,
    cursorKey,
    forceHover
}: {
    program: NeobankProgramRow;
    cursorKey?: string;
    forceHover?: boolean;
}): React.ReactElement {
    return (
        <ListItem
            itemKey={program.id}
            padding="px-4 py-3"
            gap="gap-0"
            forceHover={forceHover}
        >
            <div className="min-w-0" data-sim-cursor-key={cursorKey}>
                <div className="flex items-start gap-2">
                    {/* Dot boxed to the first line's height so it stays centered
                        on line 1 when the name wraps to two lines. */}
                    <span className="flex h-5 shrink-0 items-center md:h-6">
                        <IndicationUnit
                            variant="status"
                            state="success"
                            size={7}
                        />
                    </span>
                    <Text
                        variant="small"
                        render={<span />}
                        className="min-w-0 text-foreground-base break-words"
                    >
                        {program.name}
                    </Text>
                </div>
                <Text
                    variant="fine-print"
                    className="text-foreground-muted mt-1 pl-4"
                >
                    {program.regionLabel}
                </Text>
            </div>
        </ListItem>
    );
}

/**
 * Neobank "select a program" mobile master-detail walkthrough. Add `framed` to
 * wrap it in its own fixed-height {@link FramableProps} frame; pair it with a
 * phone-width `frameClassName` (e.g. `max-w-sm mx-auto`) at the call site.
 */
export const NeobankProgramsMobileSim = framable(
    NeobankProgramsMobileSimView,
    "NeobankProgramsMobileSim"
);

/** No sim-specific props — the tour is fully internal. */
export type NeobankProgramsMobileSimProps = FramableProps;
